import { useCallback } from 'react';

import { queryGet, queryPost, queryPut } from '../api/tools/tools';

import { useAuth } from './useAuth';
import { useFirebase } from './useFirebase';

/**
 * Talks to /api/meal-plans directly via the low-level query helpers (not useEndpoint) because
 * plan generation must work while logged out - useEndpoint's query is gated on a logged-in user.
 */
export const useMealPlanner = () => {
  const { remoteConfig } = useFirebase();
  const { user } = useAuth();
  const accessToken = user?.isAnonymous ? undefined : user?.accessToken;

  const getSlotTemplates = useCallback(
    () => queryGet(remoteConfig, '/meal-plans/slot-templates', accessToken),
    [remoteConfig, accessToken]
  );

  const calculateTargets = useCallback(
    (bodyStats) => queryPost(remoteConfig, '/meal-plans/targets', bodyStats, accessToken),
    [remoteConfig, accessToken]
  );

  const generatePlan = useCallback(
    (request) => queryPost(remoteConfig, '/meal-plans/generate', request, accessToken),
    [remoteConfig, accessToken]
  );

  const regeneratePlan = useCallback(
    (request) => queryPost(remoteConfig, '/meal-plans/regenerate', request, accessToken),
    [remoteConfig, accessToken]
  );

  const updateSelections = useCallback(
    (request) => queryPut(remoteConfig, '/meal-plans/selections', request, accessToken),
    [remoteConfig, accessToken]
  );

  const getPlanById = useCallback(
    (id) => queryGet(remoteConfig, `/meal-plans/plan/${id}`, accessToken),
    [remoteConfig, accessToken]
  );

  const savePlan = useCallback(
    (id) => queryPost(remoteConfig, `/meal-plans/${id}/save`, {}, accessToken),
    [remoteConfig, accessToken]
  );

  const getPreferences = useCallback(
    (uid) => queryGet(remoteConfig, `/users/${uid}/preferences`, accessToken),
    [remoteConfig, accessToken]
  );

  return {
    getSlotTemplates,
    calculateTargets,
    generatePlan,
    regeneratePlan,
    updateSelections,
    getPlanById,
    savePlan,
    getPreferences
  };
};
